import React, { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, Square, Minus, Plus, Upload, Headphones, Repeat } from "lucide-react";
import DeckWaveform from "@/studio/DeckWaveform";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";

function Deck({ deckId, accent, size = 150 }) {
  const { engine, loadToDeck, deckInfo } = useStudio();
  const platterRef = useRef(null);
  const rotation = useRef(0);
  const raf = useRef(null);
  const scratching = useRef(false);
  const lastAngle = useRef(0);
  const lastTime = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [cued, setCued] = useState(false);
  const [loop, setLoop] = useState(engine.decks?.[deckId]?.loop !== false);
  const [isScratching, setIsScratching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const name = deckInfo[deckId].name;
  const loaded = deckInfo[deckId].loaded;

  useEffect(() => {
    const animate = () => {
      const isPlaying = engine.started && engine.decks[deckId] && engine.decks[deckId].playing;
      // keep the Play button in sync when a non-looping track finishes on its own
      setPlaying((prev) => (prev !== isPlaying ? isPlaying : prev));
      if (!scratching.current && isPlaying) {
        rotation.current += engine.decks[deckId].rate * 4;
      }
      if (platterRef.current) platterRef.current.style.transform = `rotate(${rotation.current}deg)`;
      raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [engine, deckId]);

  const loadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio") && !/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name)) {
      toast.error("Please use an audio file (MP3, WAV, OGG…)");
      return;
    }
    try {
      await loadToDeck(deckId, file);
      toast.success(`Loaded on ${deckId === "deckA" ? "Deck A" : "Deck B"}`);
    } catch (err) {
      toast.error("Could not decode this audio file");
    }
  };
  const onFile = (e) => loadFile(e.target.files[0]);

  const doPlay = () => {
    if (!loaded) {
      toast.error("Load a track first");
      return;
    }
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    engine.startDeck(deckId);
    setPlaying(true);
  };

  const doPause = () => {
    engine.pauseDeck(deckId);
    setPlaying(false);
  };

  const doStop = () => {
    engine.cueDeck(deckId);
    setPlaying(false);
  };

  const toggleCue = () => {
    engine.ensureStarted();
    const next = !cued;
    engine.setDeckCue(deckId, next);
    setCued(next);
  };

  const toggleLoop = () => {
    engine.ensureStarted();
    const next = !loop;
    engine.setDeckLoop(deckId, next);
    setLoop(next);
  };

  const angleFrom = (e, rect) => {
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
  };

  const onDown = useCallback(
    (e) => {
      if (!engine.decks[deckId].playing) return;
      scratching.current = true;
      setIsScratching(true);
      const rect = platterRef.current.getBoundingClientRect();
      lastAngle.current = angleFrom(e, rect);
      lastTime.current = performance.now();
      const onMove = (ev) => {
        if (!scratching.current) return;
        const a = angleFrom(ev, rect);
        let delta = a - lastAngle.current;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        const now = performance.now();
        const dt = Math.max(1, now - lastTime.current);
        rotation.current += delta;
        const vel = delta / dt;
        const r = Math.max(0, Math.min(2.5, 1 + vel * 8));
        engine.scratchDeck(deckId, r);
        lastAngle.current = a;
        lastTime.current = now;
      };
      const onUp = () => {
        scratching.current = false;
        setIsScratching(false);
        engine.setDeckRate(deckId, rate);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      e.preventDefault();
    },
    [engine, deckId, rate]
  );

  const changeRate = (v) => {
    const clamped = Math.max(0.5, Math.min(1.5, Math.round(v * 100) / 100));
    setRate(clamped);
    engine.setDeckRate(deckId, clamped);
  };

  const labelSize = size * 0.34;
  const wrap = size + 46;

  return (
    <div className="flex flex-col items-center gap-3" data-testid={`turntable-${deckId}`}>
      <div className="flex items-center gap-2 w-full justify-between" style={{ maxWidth: wrap }}>
        <span className="font-display font-black text-sm tracking-widest" style={{ color: accent }}>
          {deckId === "deckA" ? "DECK A" : "DECK B"}
        </span>
        <label
          className="cursor-pointer flex items-center gap-1 text-[10px] hw-label hover:text-white transition-colors"
          data-testid={`load-${deckId}-label`}
        >
          <Upload size={12} /> Load
          <input type="file" accept="audio/*" className="hidden" onChange={onFile} data-testid={`load-${deckId}-input`} />
        </label>
      </div>

      {/* Turntable base */}
      <div
        className="relative rounded-2xl transition-all"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          loadFile(e.dataTransfer.files[0]);
        }}
        style={{
          width: wrap,
          height: wrap,
          background: "linear-gradient(145deg,#232323,#0e0e0e)",
          border: dragOver ? `2px dashed ${accent}` : "1px solid #000",
          boxShadow: dragOver
            ? `0 0 24px ${accent}88, inset 0 1px 1px rgba(255,255,255,0.05)`
            : "0 10px 24px rgba(0,0,0,0.7), inset 0 1px 1px rgba(255,255,255,0.05)",
        }}
      >
        {dragOver && (
          <div className="absolute inset-0 z-40 rounded-2xl flex items-center justify-center bg-black/50 pointer-events-none">
            <span className="hw-label" style={{ color: accent }}>Drop track to load</span>
          </div>
        )}
        {/* power LED */}
        <div
          className="absolute rounded-full"
          style={{ left: 12, bottom: 12, width: 7, height: 7, background: playing ? "#00ff66" : "#333", boxShadow: playing ? "0 0 8px #00ff66" : "none" }}
        />
        <span className="absolute left-[26px] bottom-[9px] hw-label text-[7px]">{playing ? "PLAY" : "PAUSE"}</span>

        {/* Record */}
        <div
          className="absolute rounded-full cursor-grab active:cursor-grabbing"
          style={{
            width: size,
            height: size,
            left: (wrap - size) / 2,
            top: (wrap - size) / 2,
            touchAction: "none",
            boxShadow: playing ? `0 0 24px ${accent}55, inset 0 0 40px rgba(0,0,0,0.95)` : "inset 0 0 40px rgba(0,0,0,0.95)",
          }}
          onPointerDown={onDown}
        >
          <div
            ref={platterRef}
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "repeating-radial-gradient(circle at center, #0c0c0c 0px, #0c0c0c 1.5px, #050505 1.5px, #050505 3px), radial-gradient(circle at 38% 32%, #202020 0%, #050505 60%)",
              filter: isScratching ? "blur(0.9px)" : "none",
              transition: "filter 0.1s",
            }}
          >
            {/* dust & wear */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                opacity: 0.14,
                mixBlendMode: "screen",
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
              }}
            />
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle at 64% 38%, rgba(255,255,255,0.06) 0%, transparent 20%)" }}
            />
            {/* record label */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center"
              style={{
                width: labelSize,
                height: labelSize,
                background: `radial-gradient(circle at 40% 35%, ${accent}, ${accent}cc 60%, ${accent}99)`,
                boxShadow: "inset 0 0 8px rgba(0,0,0,0.4)",
              }}
            >
              <span className="font-display font-black text-black/80 leading-none" style={{ fontSize: labelSize * 0.28 }}>
                {deckId === "deckA" ? "A" : "B"}
              </span>
              <span className="text-black/50 font-mono-data leading-none mt-0.5" style={{ fontSize: labelSize * 0.09 }}>
                BEATMXR
              </span>
              {/* spindle */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 6, height: 6, background: "#c0c0c0", boxShadow: "0 0 2px #000" }} />
            </div>
          </div>

          {/* fixed glossy sheen (light reflection) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: "linear-gradient(125deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.05) 100%)",
            }}
          />
          {/* outer rim */}
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: `2px solid ${accent}66` }} />
        </div>

        {/* Tonearm */}
        <div className="absolute" style={{ top: 10, right: 10, width: 18, height: 18, zIndex: 20 }}>
          <div
            className="absolute rounded-full"
            style={{ width: 18, height: 18, right: 0, top: 0, background: "linear-gradient(145deg,#6a6a6a,#242424)", border: "1px solid #000" }}
          />
          <div
            className="origin-top-right transition-transform duration-700"
            style={{
              position: "absolute",
              top: 9,
              right: 9,
              width: 5,
              height: size * 0.6,
              borderRadius: 3,
              background: "linear-gradient(90deg,#b0b0b0,#585858)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.6)",
              transform: `rotate(${playing ? 26 : 8}deg)`,
            }}
          >
            <div
              className="absolute"
              style={{ bottom: -7, left: -7, width: 16, height: 11, borderRadius: 2, background: "#141414", border: `1px solid ${accent}` }}
            />
          </div>
        </div>
      </div>

      <div className="font-mono-data text-[10px] text-neutral-500 truncate text-center" style={{ width: wrap }}>
        {name}
      </div>

      {/* waveform + timer */}
      <div style={{ width: wrap }}>
        <DeckWaveform deckId={deckId} accent={accent} />
      </div>

      {/* transport: Play / Pause / Stop */}
      <div className="flex items-center gap-1.5" style={{ width: wrap }}>
        <button
          data-testid={`deck-play-${deckId}`}
          onClick={doPlay}
          title="Play"
          className="flex-1 h-9 rounded-sm border flex items-center justify-center gap-1.5 text-[10px] font-bold transition-colors"
          style={playing ? { borderColor: accent, background: `${accent}22`, color: accent } : { borderColor: "#404040", color: "#d4d4d4" }}
        >
          <Play size={13} fill="currentColor" /> PLAY
        </button>
        <button
          data-testid={`deck-pause-${deckId}`}
          onClick={doPause}
          title="Pause — holds its spot so you can drop it in on beat"
          className="flex-1 h-9 rounded-sm border border-[#404040] text-neutral-300 flex items-center justify-center gap-1.5 text-[10px] font-bold hover:bg-[#242424] transition-colors"
        >
          <Pause size={13} fill="currentColor" /> PAUSE
        </button>
        <button
          data-testid={`deck-stop-${deckId}`}
          onClick={doStop}
          title="Stop — rewind to the start"
          className="flex-1 h-9 rounded-sm border border-[#404040] text-neutral-300 flex items-center justify-center gap-1.5 text-[10px] font-bold hover:bg-[#242424] transition-colors"
        >
          <Square size={12} fill="currentColor" /> STOP
        </button>
        <button
          data-testid={`deck-cue-${deckId}`}
          onClick={toggleCue}
          title="Cue — pre-listen this deck in your headphones (set up Headphone Cue below)"
          className="flex-1 h-9 rounded-sm border flex items-center justify-center gap-1.5 text-[10px] font-bold transition-colors"
          style={cued ? { borderColor: accent, background: `${accent}22`, color: accent } : { borderColor: "#404040", color: "#d4d4d4" }}
        >
          <Headphones size={12} /> CUE
        </button>
      </div>

      {/* speed / pitch */}
      <div className="flex items-center gap-1.5" style={{ width: wrap }}>
        <button
          data-testid={`deck-slower-${deckId}`}
          onClick={() => changeRate(rate - 0.02)}
          title="Slower"
          className="w-8 h-8 rounded-sm border border-[#404040] text-neutral-300 flex items-center justify-center hover:bg-[#242424] transition-colors shrink-0"
        >
          <Minus size={13} />
        </button>
        <div className="flex-1 flex flex-col gap-0.5">
          <div className="flex justify-between hw-label text-[8px]">
            <span>Speed</span>
            <button
              data-testid={`deck-speed-reset-${deckId}`}
              onClick={() => changeRate(1)}
              className="font-mono-data hover:underline"
              style={{ color: accent }}
              title="Reset to normal speed"
            >
              {rate.toFixed(2)}x
            </button>
          </div>
          <input
            data-testid={`pitch-${deckId}`}
            type="range"
            min={0.5}
            max={1.5}
            step={0.01}
            value={rate}
            onChange={(e) => changeRate(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <button
          data-testid={`deck-faster-${deckId}`}
          onClick={() => changeRate(rate + 0.02)}
          title="Faster"
          className="w-8 h-8 rounded-sm border border-[#404040] text-neutral-300 flex items-center justify-center hover:bg-[#242424] transition-colors shrink-0"
        >
          <Plus size={13} />
        </button>
        <button
          data-testid={`deck-loop-${deckId}`}
          onClick={toggleLoop}
          title={loop ? "Loop is ON — track repeats. Click to play once and stop." : "Loop is OFF — track plays once. Click to repeat."}
          className="w-8 h-8 rounded-sm border flex items-center justify-center transition-colors shrink-0"
          style={loop ? { borderColor: accent, background: `${accent}22`, color: accent } : { borderColor: "#404040", color: "#8a8a8a" }}
        >
          <Repeat size={13} />
        </button>
      </div>
    </div>
  );
}

function Crossfader() {
  const { engine } = useStudio();
  const [x, setX] = useState(0);
  // Keep the slider in sync when the crossfade is moved programmatically (auto hand-off).
  useEffect(() => {
    const t = setInterval(() => {
      setX((prev) => (typeof engine.crossfade === "number" && engine.crossfade !== prev ? engine.crossfade : prev));
    }, 200);
    return () => clearInterval(t);
  }, [engine]);
  return (
    <div className="mt-8 flex flex-col items-center gap-1.5 w-full" data-testid="crossfader">
      <div className="flex justify-between items-center" style={{ width: 280 }}>
        <span className="font-display font-black text-xs" style={{ color: "#00e5ff" }}>A</span>
        <span className="hw-label text-[9px]">Crossfader</span>
        <span className="font-display font-black text-xs" style={{ color: "#ff007f" }}>B</span>
      </div>
      <input
        data-testid="crossfader-slider"
        type="range"
        min={-1}
        max={1}
        step={0.01}
        value={x}
        title="Blend between Deck A and Deck B"
        onChange={(e) => {
          const v = Number(e.target.value);
          setX(v);
          engine.ensureStarted();
          engine.setCrossfade(v);
        }}
        style={{ width: 280 }}
      />
    </div>
  );
}

export default function Turntables({ size = 150, layout = "column" }) {
  return (
    <div className={layout === "row" ? "" : "metal-panel border-b border-[#262626] p-4"}>
      {layout !== "row" && (
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="h-px flex-1 bg-[#262626]" />
          <span className="hw-label">DJ Turntables — drag platter to scratch</span>
          <div className="h-px flex-1 bg-[#262626]" />
        </div>
      )}
      <div className={`flex ${layout === "row" ? "gap-10 justify-center" : "justify-around"} items-start gap-4 flex-wrap`}>
        <Deck deckId="deckA" accent="#00e5ff" size={size} />
        <Deck deckId="deckB" accent="#ff007f" size={size} />
      </div>
      {layout === "row" && (
        <div className="flex justify-center">
          <Crossfader />
        </div>
      )}
    </div>
  );
}
